<template>
    <div class="qr-code-container">
        <div class="qr-code-wrapper" v-if="qrCodeUrl">
            <div id="qrcode" ref="qrcode"></div>
            <div class="branch-info">
                <h4>{{ branch.branch_name_en }}</h4>
                <p>{{ branch.branch_code }}</p>
            </div>
        </div>

        <div class="qr-actions">
            <Button type="primary" @click="generateQRCode" :loading="generating">
                <Icon type="ios-refresh" />
                {{ generating ? $t("branch.generating") : $t("branch.regenerate") }}
            </Button>
            <Button type="success" @click="downloadQRCode">
                <Icon type="ios-download" />
                {{ $t("branch.download") }}
            </Button>
        </div>

        <div class="qr-info">
            <p><strong>{{ $t("branch.qrCodeUrl") }}:</strong></p>
            <Input 
                v-model="qrCodeUrl" 
                readonly 
                type="textarea" 
                :autosize="{ minRows: 2, maxRows: 4 }"
            />
        </div>
    </div>
</template>

<script>
import QRCode from "qrcodejs2";
import axios from "axios";
import VueCookies from "vue-cookies";

export default {
    name: "QRCodeGenerator",
    props: {
        branch: {
            type: Object,
            default: () => null
        }
    },
    data() {
        return {
            qrCodeUrl: "",
            qrcode: null,
            generating: false
        };
    },
    mounted() {
        if (this.branch && this.branch.branch_id) {
            this.generateQRCode();
        }
    },
    watch: {
        branch: {
            handler(newVal) {
                if (newVal && newVal.branch_id) {
                    this.generateQRCode();
                }
            },
            deep: true
        }
    },
    methods: {
        async generateQRCode() {
            if (!this.branch || !this.branch.branch_id) {
                this.$Message.warning(this.$t("branch.selectBranchFirst"));
                return;
            }

            this.generating = true;
            try {
                const token = VueCookies.get("token");
                
                // Get encrypted token from backend
                const response = await axios.post(
                    `${process.env.VUE_APP_PROXY}/api/branches/${this.branch.branch_id}/generate-qr-token`,
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );

                if (response.data.success) {
                    const qrToken = response.data.data.token;
                    this.qrCodeUrl = `${window.location.origin}/qr-code-scan-display?token=${qrToken}`;

                    // Clear previous QR code
                    if (this.$refs.qrcode) {
                        this.$refs.qrcode.innerHTML = "";
                    }

                    // Generate new QR code
                    this.$nextTick(() => {
                        this.qrcode = new QRCode(this.$refs.qrcode, {
                            text: this.qrCodeUrl,
                            width: 256,
                            height: 256,
                            colorDark: "#000000",
                            colorLight: "#ffffff",
                            correctLevel: QRCode.CorrectLevel.H
                        });
                    });

                    this.$Message.success(this.$t("branch.qrCodeGenerated"));
                } else {
                    this.$Message.error(
                        response.data.message || this.$t("branch.qrCodeFailed")
                    );
                }
            } catch (error) {
                this.$Message.error(this.$t("branch.qrCodeFailed"));
                console.error("QR Code generation error:", error);
            } finally {
                this.generating = false;
            }
        },
        downloadQRCode() {
            const canvas = this.$refs.qrcode.querySelector("canvas");
            if (canvas) {
                const url = canvas.toDataURL("image/png");
                const link = document.createElement("a");
                link.download = `qr-code-${this.branch.branch_code}.png`;
                link.href = url;
                link.click();
                this.$Message.success(this.$t("branch.qrCodeDownloaded"));
            }
        },
    }
};
</script>

<style scoped lang="scss">
.qr-code-container {
    padding: 20px;
    text-align: center;

    .qr-code-wrapper {
        margin-bottom: 20px;
        display: inline-block;

        #qrcode {
            padding: 20px;
            background: white;
            border: 1px solid #dcdee2;
            border-radius: 4px;
            display: inline-block;
        }

        .branch-info {
            margin-top: 15px;

            h4 {
                margin: 5px 0;
                color: #2d8cf0;
            }

            p {
                margin: 5px 0;
                color: #808695;
                font-weight: bold;
            }
        }
    }

    .qr-actions {
        margin-bottom: 20px;
        display: flex;
        gap: 10px;
        justify-content: center;
    }

    .qr-info {
        margin-top: 20px;
        text-align: left;

        p {
            margin-bottom: 10px;
            color: #515a6e;
        }
    }
}
</style>